<script setup>
import { computed } from 'vue';
import { RouterLink } from 'vue-router';
import { buttonVariants } from '@/components/ui/variants';

const props = defineProps({
  to: { type: [String, Object], default: null },
  href: { type: String, default: null },
  type: { type: String, default: 'button' },
  intent: { type: String, default: 'primary' },
  size: { type: String, default: 'md' },
  icon: { type: String, default: null },
  trailingIcon: { type: String, default: null },
  iconOnly: { type: Boolean, default: false },
  block: { type: Boolean, default: false },
  active: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
  external: { type: Boolean, default: false },
});

const tag = computed(() => (props.to ? RouterLink : props.href ? 'a' : 'button'));

const attrs = computed(() => {
  if (props.to) return { to: props.to };
  if (props.href) {
    return props.external ? { href: props.href, target: '_blank', rel: 'noopener' } : { href: props.href };
  }
  return { type: props.type, disabled: props.loading || undefined };
});

const classes = computed(() =>
  buttonVariants({
    intent: props.intent,
    size: props.size,
    iconOnly: props.iconOnly,
    block: props.block,
    active: props.active,
  })
);

const iconSize = computed(() => ({ sm: 'text-base', md: 'text-lg', lg: 'text-xl' })[props.size]);
</script>

<template>
  <component :is="tag" v-bind="attrs" :class="classes" :aria-busy="loading || undefined">
    <iconify-icon v-if="loading" icon="svg-spinners:ring-resize" :class="iconSize" aria-hidden="true" />
    <iconify-icon v-else-if="icon" :icon="icon" :class="iconSize" aria-hidden="true" />
    <slot />
    <iconify-icon v-if="trailingIcon" :icon="trailingIcon" :class="iconSize" aria-hidden="true" />
  </component>
</template>
